package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.domain.model.GmInventoryItem
import fr.manyverse.app.domain.model.GmInventoryItemToCreate
import fr.manyverse.app.domain.model.ItemCategory
import fr.manyverse.app.domain.repository.GmInventoryRepository
import fr.manyverse.app.infrastructure.error.mapToDomainException
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Columns
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

private const val GM_INVENTORY_TABLE = "gm_inventory"

class SupabaseGmInventoryRepository(
    private val supabaseClient: SupabaseClient
) : GmInventoryRepository {

    override suspend fun getItemsForTable(tableId: String): Result<List<GmInventoryItem>> {
        return try {
            val items = supabaseClient
                .from(GM_INVENTORY_TABLE)
                .select(Columns.ALL) {
                    filter { eq("table_id", tableId) }
                }
                .decodeList<GmInventoryItem>()
            Result.success(items)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "gm_inventory"))
        }
    }

    override suspend fun createItem(item: GmInventoryItemToCreate): Result<GmInventoryItem> {
        return try {
            val insertDto = GmInventoryItemInsertDto(
                tableId = item.tableId,
                name = item.name,
                category = item.category.name,
                quantity = item.quantity,
                imageUrl = item.imageUrl
            )
            val created = supabaseClient
                .from(GM_INVENTORY_TABLE)
                .insert(insertDto) { select() }
                .decodeSingle<GmInventoryItem>()
            Result.success(created)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "gm_inventory"))
        }
    }

    override suspend fun updateItem(
        itemId: String,
        name: String,
        category: ItemCategory,
        quantity: Int
    ): Result<GmInventoryItem> {
        return try {
            val updateDto = GmInventoryItemUpdateDto(
                name = name,
                category = category.name,
                quantity = quantity
            )
            val updated = supabaseClient
                .from(GM_INVENTORY_TABLE)
                .update(updateDto) {
                    filter { eq("id", itemId) }
                    select()
                }
                .decodeSingle<GmInventoryItem>()
            Result.success(updated)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "gm_inventory"))
        }
    }

    override suspend fun deleteItem(itemId: String): Result<Unit> {
        return try {
            supabaseClient
                .from(GM_INVENTORY_TABLE)
                .delete { filter { eq("id", itemId) } }
            Result.success(Unit)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "gm_inventory"))
        }
    }

    override suspend fun updateImageUrl(itemId: String, imageUrl: String?): Result<GmInventoryItem> {
        return try {
            val updated = supabaseClient
                .from(GM_INVENTORY_TABLE)
                .update(mapOf("image_url" to imageUrl)) {
                    filter { eq("id", itemId) }
                    select()
                }
                .decodeSingle<GmInventoryItem>()
            Result.success(updated)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "gm_inventory"))
        }
    }

    @Serializable
    private data class GmInventoryItemUpdateDto(
        @SerialName("name")
        val name: String,

        @SerialName("category")
        val category: String,

        @SerialName("quantity")
        val quantity: Int
    )

    @Serializable
    private data class GmInventoryItemInsertDto(
        @SerialName("table_id")
        val tableId: String,

        @SerialName("name")
        val name: String,

        @SerialName("category")
        val category: String,

        @SerialName("quantity")
        val quantity: Int,

        @SerialName("image_url")
        val imageUrl: String? = null
    )
}
